import { normalizeCpf } from "src-next/shared/utils/cpf";
import type { MessagingPatient } from "src-next/modules/crm-clinical/messaging/types";
import { isVetApp } from "../../messaging/helpers/isVetApp";

import type { CrmPatientSummary } from "../types";
import { isLogicalCpfPatientId } from "./crmVetTutorRecipient";

/**
 * Converte resumo CRM (PatientOrganizationContext / agregações) para o contrato
 * de envio do módulo messaging (`SendWhatsAppMessageDialog` / FluxZap).
 */
export function mapCrmPatientSummaryToMessagingPatient(
  patient: CrmPatientSummary,
): MessagingPatient {
  const patientId = String(patient.patientId ?? "").trim();
  const cpfRaw = String(patient.taxIdentifierValue ?? "").trim();
  const cpfDigits = cpfRaw ? normalizeCpf(cpfRaw) : "";

  const vetTutorRecipient = isVetApp() && isLogicalCpfPatientId(patientId);

  return {
    id: patientId,
    fullName:
      String(patient.usualName ?? "").trim() ||
      String(patient.officialName ?? "").trim() ||
      patientId,
    cpf: cpfDigits.length === 11 ? cpfDigits : cpfRaw || undefined,
    phone: patient.phone?.trim() || undefined,
    birthDate: patient.birthDate?.trim() || undefined,
    /**
     * Tutor vet: deixar `forReference` vazio para o FluxZap montar destino por CPF.
     * Pet/humano: mantém referência explícita do Patient da organização.
     */
    forReference: vetTutorRecipient
      ? undefined
      : patientId
        ? `Patient/${patientId}`
        : undefined,
    isTutor: vetTutorRecipient ? true : undefined,
  };
}

/** Referência FHIR para Task de questionário após envio (paciente único). */
export function taskPatientReferenceFromCrmPatient(
  patient: CrmPatientSummary,
): string | null {
  const id = String(patient.patientId ?? "").trim();
  return id ? `Patient/${id}` : null;
}

/**
 * Resolve pacientes a partir de IDs (lookup CRM) para abrir o dialog de envio.
 * IDs sem lookup recebem resumo mínimo (nome = id) para não bloquear a UI.
 */
export function crmPatientsFromIds(
  ids: Iterable<string>,
  lookup: Map<string, CrmPatientSummary>,
): CrmPatientSummary[] {
  const result: CrmPatientSummary[] = [];
  for (const id of ids) {
    const trimmed = String(id ?? "").trim();
    if (!trimmed) continue;
    const found = lookup.get(trimmed);
    if (found) {
      result.push(found);
      continue;
    }
    result.push({
      patientId: trimmed,
      officialName: trimmed,
      birthDate: "",
    });
  }
  return result;
}
